import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  getSocialAiSettings,
  updateSocialAiSettings,
  generateDraftsNow,
} from "@/lib/social-ai-settings.functions";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/social/settings")({
  component: SettingsPage,
  ssr: false,
  head: () => ({ meta: [{ title: "Social AI settings · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
});

function SettingsPage() {
  const get = useServerFn(getSocialAiSettings);
  const save = useServerFn(updateSocialAiSettings);
  const runNow = useServerFn(generateDraftsNow);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["social-ai-settings"], queryFn: () => get() });

  const [freq, setFreq] = useState<"daily" | "weekly" | "monthly" | "off">("daily");
  const [count, setCount] = useState(3);
  const [autoImg, setAutoImg] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = q.data?.settings;
    if (!s) return;
    setFreq(s.frequency as "daily" | "weekly" | "monthly" | "off");
    setCount(s.posts_per_run);
    setAutoImg(s.auto_generate_images);
  }, [q.data]);

  const persist = async () => {
    setBusy(true);
    try {
      await save({ data: { frequency: freq, posts_per_run: count, auto_generate_images: autoImg } });
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["social-ai-settings"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await runNow({ data: { count } });
      toast.success(`Generated ${r.inserted} drafts — review in AI drafts tab`);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to generate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">AI content schedule</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick how often AI builds draft posts. They land in <strong>AI drafts</strong> for you to
          review and approve before anything goes live.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border border-border p-5">
        <div className="space-y-2">
          <Label>Frequency</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { v: "daily", label: "Daily", hint: "New drafts every day" },
              { v: "weekly", label: "Weekly", hint: "Built Saturday for Sun–Sat" },
              { v: "monthly", label: "Monthly", hint: "Built the 28th for the 1st" },
              { v: "off", label: "Off", hint: "Pause auto-generation" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setFreq(o.v as any)}
                className={`rounded-lg border p-3 text-left text-sm transition ${
                  freq === o.v
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-primary/40"
                }`}
              >
                <div className="font-semibold">{o.label}</div>
                <div className="text-xs text-muted-foreground">{o.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="count">Posts per run</Label>
            <Input
              id="count"
              type="number"
              min={1}
              max={20}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            />
            <p className="text-xs text-muted-foreground">
              Each post covers a different product so the feed never repeats the same topic.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center justify-between">
              Auto-generate brand images
              <Switch checked={autoImg} onCheckedChange={setAutoImg} />
            </Label>
            <p className="text-xs text-muted-foreground">
              A square graphic is created in your brand colors and attached to each draft.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={persist} disabled={busy}>Save schedule</Button>
          <Button onClick={generate} disabled={busy} variant="secondary">
            Generate drafts now
          </Button>
        </div>

        {q.data?.settings?.last_generated_at && (
          <p className="text-xs text-muted-foreground">
            Last auto-run: {new Date(q.data.settings.last_generated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
